import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
// --- Styled Components --- 
const RoadmapContainer = styled.div`
  padding: 20px;
  font-family: sans-serif;
  border: 1px solid #ddd;
  background-color: #fff;
  overflow: auto; /* SVG가 클 경우 스크롤 */
`;
const RoadmapTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;
const StyledSvg = styled.svg`
  display: block; // 불필요한 공백 제거
  margin: auto; // 중앙 정렬 시도
`;
// 기본 섹션용 g 요소 스타일링
const SectionGroup = styled.g`
  /* 기본 스타일 */
  rect.section-bg {
    stroke: #000;
    stroke-width: 1;
    fill: #ffeeba; /* Default background */
  }
  
  rect.highlight-bg {
    fill: #ffff00; /* Highlighted section background */
  }
  
  text {
    font-size: 12px;
    dominant-baseline: middle;
  }
  
  text.title {
    font-weight: bold;
    font-size: 14px;
  }
`;
const Roadmap = ({ title, sections, connections, width = 800, height = 900 })=>{
    // 섹션 ID로 섹션 데이터를 찾는 헬퍼 함수
    const findSectionById = (id)=>{
        return sections.find((section)=>section.id === id);
    };
    // 섹션의 크기를 계산하는 함수 (섹션 내용에 따라 다름)
    const calculateSectionSize = (section)=>{
        const defaultWidth = 180;
        // 아이템이 비어있는 섹션은 더 작게
        if (section.items.length === 0) {
            // Python 메인 노드는 특별히 처리
            if (section.id === 'python_main') {
                return {
                    width: 100,
                    height: 60
                };
            }
            // 다른 일반 아이템 노드 (길이에 따라 너비 조정)
            const titleLength = section.title.length;
            const width = Math.max(80, Math.min(200, titleLength * 8));
            return {
                width,
                height: 40
            };
        }
        // 아이템이 있는 섹션은 크기 계산
        const itemHeight = 30; // 아이템 하나당 높이
        const headerHeight = 40; // 헤더 영역 높이
        const padding = 20; // 위아래 패딩
        const height = headerHeight + section.items.length * itemHeight + padding;
        return {
            width: defaultWidth,
            height
        };
    };
    // 연결선의 시작점/끝점을 계산하는 함수
    const calculateAnchorPoint = (sectionId, side)=>{
        const section = findSectionById(sectionId);
        if (!section) {
            console.warn(`Section not found: ${sectionId}`);
            return {
                x: 0,
                y: 0
            };
        }
        const sectionSize = calculateSectionSize(section);
        switch(side){
            case 'top':
                return {
                    x: section.x + sectionSize.width / 2,
                    y: section.y
                };
            case 'bottom':
                return {
                    x: section.x + sectionSize.width / 2,
                    y: section.y + sectionSize.height
                };
            case 'left':
                return {
                    x: section.x,
                    y: section.y + sectionSize.height / 2
                };
            case 'right':
                return {
                    x: section.x + sectionSize.width,
                    y: section.y + sectionSize.height / 2
                };
            default:
                return {
                    x: section.x + sectionSize.width / 2,
                    y: section.y + sectionSize.height / 2
                };
        }
    };
    // 연결선 SVG 경로를 생성하는 함수
    const createConnectionPath = (conn)=>{
        const fromSection = findSectionById(conn.from);
        const toSection = findSectionById(conn.to);
        if (!fromSection || !toSection) {
            console.warn(`Connection ignored: Cannot find sections for ${conn.from} -> ${conn.to}`);
            return /*#__PURE__*/ _jsx(_Fragment, {});
        }
        // 지정된 방향 또는 기본 방향 사용
        const fromSide = conn.fromSide || 'bottom';
        const toSide = conn.toSide || 'top';
        // 시작점과 끝점 계산
        const start = calculateAnchorPoint(conn.from, fromSide);
        const end = calculateAnchorPoint(conn.to, toSide);
        // 경로 유형에 따라 다른 SVG 요소 반환
        if (conn.pathType === 'curve') {
            // 베지어 곡선 경로를 위한 컨트롤 포인트 계산
            // 간단한 자동 계산 - 실제로는 더 복잡한 로직 필요할 수 있음
            const controlPoint1 = {
                x: start.x,
                y: start.y + Math.abs(end.y - start.y) / 2
            };
            const controlPoint2 = {
                x: end.x,
                y: end.y - Math.abs(end.y - start.y) / 2
            };
            // 사용자 지정 제어점이 있다면 사용
            if (conn.controlPoints) {
                if (conn.controlPoints.x1 !== undefined) controlPoint1.x = conn.controlPoints.x1;
                if (conn.controlPoints.y1 !== undefined) controlPoint1.y = conn.controlPoints.y1;
                if (conn.controlPoints.x2 !== undefined) controlPoint2.x = conn.controlPoints.x2;
                if (conn.controlPoints.y2 !== undefined) controlPoint2.y = conn.controlPoints.y2;
            }
            return /*#__PURE__*/ _jsx("path", {
                d: `M ${start.x} ${start.y} C ${controlPoint1.x} ${controlPoint1.y}, ${controlPoint2.x} ${controlPoint2.y}, ${end.x} ${end.y}`,
                stroke: "#007bff",
                strokeWidth: 2,
                strokeDasharray: conn.isDotted ? "5,5" : undefined,
                fill: "none"
            }, `path-${conn.from}-${conn.to}`);
        }
        // 기본: 직선
        return /*#__PURE__*/ _jsx("line", {
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y,
            stroke: "#007bff",
            strokeWidth: 2,
            strokeDasharray: conn.isDotted ? "5,5" : undefined
        }, `line-${conn.from}-${conn.to}`);
    };
    return /*#__PURE__*/ _jsxs(RoadmapContainer, {
        children: [
            /*#__PURE__*/ _jsx(RoadmapTitle, {
                children: title
            }),
            /*#__PURE__*/ _jsxs(StyledSvg, {
                width: width,
                height: height,
                viewBox: `0 0 ${width} ${height}`,
                children: [
                    connections.map((conn, index)=>/*#__PURE__*/ _jsx(React.Fragment, {
                            children: createConnectionPath(conn)
                        }, `conn-${index}`)),
                    sections.map((section)=>{
                        const sectionSize = calculateSectionSize(section);
                        const isMainNode = section.id === 'python_main';
                        const hasItems = section.items.length > 0;
                        return /*#__PURE__*/ _jsxs(SectionGroup, {
                            transform: `translate(${section.x}, ${section.y})`,
                            children: [
                                /*#__PURE__*/ _jsx("rect", {
                                    className: section.isHighlighted ? 'section-bg highlight-bg' : 'section-bg',
                                    width: sectionSize.width,
                                    height: sectionSize.height,
                                    rx: "5"
                                }),
                                /*#__PURE__*/ _jsx("text", {
                                    className: "title",
                                    x: sectionSize.width / 2,
                                    y: hasItems ? 20 : sectionSize.height / 2,
                                    textAnchor: "middle",
                                    children: section.title
                                }),
                                hasItems && section.items.map((item, index)=>/*#__PURE__*/ _jsxs("g", {
                                        transform: `translate(10, ${40 + index * 30})`,
                                        children: [
                                            /*#__PURE__*/ _jsx("rect", {
                                                x: "0",
                                                y: "-15",
                                                width: sectionSize.width - 20,
                                                height: "25",
                                                fill: "#ffeeba",
                                                rx: "3",
                                                stroke: "#000",
                                                strokeWidth: "0.5"
                                            }),
                                            /*#__PURE__*/ _jsx("text", {
                                                x: 5,
                                                y: "0",
                                                dominantBaseline: "middle",
                                                fontSize: "10px",
                                                children: item.label
                                            })
                                        ]
                                    }, item.id))
                            ]
                        }, section.id);
                    })
                ]
            })
        ]
    });
};
export default Roadmap;
